import { BREAKPOINTS, Button, ExpandablePanel, H2, MessageCard, Modal, TextInput, useDebounce } from '@manulife/mux';
import { useFormik } from 'formik';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled, { css } from 'styled-components';
import * as Yup from 'yup';
import useAEMContent, { FRAGMENT_MP } from '../../CustomHooks/useAEMContent';
import useShallowEqualSelector from '../../CustomHooks/useShallowEqualSelector';
import BankModalLoadingIndicator from '../LoadingIndicator/BankModalLoadingIndicator';
import FieldLoadingIndicator from '../LoadingIndicator/FieldLoadingIndicator';
import { ButtonWrapper, ErrorMsg, ValidationAlertWrapper } from '../Shared/SharedStyles';
import { GetBank } from './ReducerActions/GetBank';
import { ResetBank } from './ReducerActions/ResetBank';
import { UpdateMemberBankAccount } from './ReducerActions/UpdateMemberBankAccount';
import { ADOBE } from '../../utils/AppConstants';

const BoldLabel = styled.strong`
    display: block;
    margin-top: 15px;
    margin-bottom: 5px;
`;

const List = styled.ul`
    padding-left: 20px;
    margin-top: 0px;
`;

const media = Object.keys(BREAKPOINTS).reduce((acc, label) => {
    acc[label] = (...args) => css`
        @media (max-width: ${BREAKPOINTS[label] / 16}em) {
            ${css(...args)}
        }
    `;
    return acc;
}, {});

const TextInputBankWrapper = styled.div`
    margin-top: 15px;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    column-gap: 12px;
    ${media.TABLET_PORTRAIT`
        display: block;
        > * {
            margin-top: 15px;
        }
    `}
`;

const AEM_IMG_URL = `${window.__ENV__.BFF_URL}/aem/v2/image`;
const MIN_BRANCH_NUMBER_LENGTH = 5;
const MIN_INSTITUTION_NUMBER_LENGTH = 3;
const MIN_ACCOUNT_NUMBER_LENGTH = 7;

const BankingModal = () => {
    const {
        isLoadingFeatures,
        isLoadingMemberData,
        member,
        isLoadingBankData,
        isBankValid,
        isSavingBankInformation,
        bankName,
        errorLoad,
        errorUpdate,
        isCsr,
        features,
        isCiamSession,
        isSteppedUp,
    } = useShallowEqualSelector((state) => ({
        isLoadingFeatures: state.Features.isLoading,
        isLoadingMemberData: state.MemberData.isLoadingMemberData,
        member: state.MemberData.member,
        isLoadingBankData: state.BankData.isLoadingBankData,
        isBankValid: state.BankData.isBankValid,
        isSavingBankInformation: state.BankData.isSavingBankInformation,
        bankName: state.BankData.bankName,
        errorLoad: state.Errors.bankDataLoad,
        errorUpdate: state.Errors.bankDataUpdate,
        isCsr: state.Authentication.isCsr,
        features: state.Features.data,
        isCiamSession: state.Authentication.isCiamSession,
        isSteppedUp: state.Authentication.isSteppedUp,
    }));

    const history = useHistory();
    const dispatch = useDispatch();
    const errorRef = useRef();

    const headerLabel = useAEMContent(FRAGMENT_MP.bankInfo, 'header');
    const bankInformationMessage = useAEMContent(FRAGMENT_MP.bankInfo, 'bankInformationMessage', { parseHTML: true });
    const onlineInstructionOne = useAEMContent(FRAGMENT_MP.bankInfo, 'onlineInstructionOne');
    const onlineInstructionTwo = useAEMContent(FRAGMENT_MP.bankInfo, 'onlineInstructionTwo');
    const onlineInstructionThree = useAEMContent(FRAGMENT_MP.bankInfo, 'onlineInstructionThree');
    const accordianTitle = useAEMContent(FRAGMENT_MP.bankInfo, 'accordianTitle', { parseHTML: true });
    const online = useAEMContent(FRAGMENT_MP.bankInfo, 'online');
    const personalCheque = useAEMContent(FRAGMENT_MP.bankInfo, 'personalCheque');
    const chequeAltText = useAEMContent(FRAGMENT_MP.bankInfo, 'chequeAltText');
    const branchNumberLabel = useAEMContent(FRAGMENT_MP.bankInfo, 'branchLabel');
    const institutionNumberLabel = useAEMContent(FRAGMENT_MP.bankInfo, 'institutionLabel');
    const accountNumberLabel = useAEMContent(FRAGMENT_MP.bankInfo, 'accountLabel');
    const bankNameLabel = useAEMContent(FRAGMENT_MP.bankInfo, 'bankNameLabel');
    const bankNotFound = useAEMContent(FRAGMENT_MP.bankInfo, 'bankNotFound');
    const chequeImagePath = useAEMContent(FRAGMENT_MP.bankInfo, 'chequeImagePath');

    const errorSummaryTitle = useAEMContent(FRAGMENT_MP.validationMessages, 'errorSummaryTitle');

    const bankValidationMessage = useAEMContent(FRAGMENT_MP.bankValidationErrors, 'bankValidationMessage');
    const validBranch = useAEMContent(FRAGMENT_MP.bankValidationErrors, 'validBranch');
    const validInstitution = useAEMContent(FRAGMENT_MP.bankValidationErrors, 'validInstitution');
    const validAccount = useAEMContent(FRAGMENT_MP.bankValidationErrors, 'validAccount');

    const saveButton = useAEMContent(FRAGMENT_MP.common, 'saveButton');
    const cancelButton = useAEMContent(FRAGMENT_MP.common, 'cancelButton');

    const validationSchema = Yup.object({
        isBankValid: Yup.boolean(),
        branchNumber: Yup.string()
            .required(validBranch)
            .min(MIN_BRANCH_NUMBER_LENGTH, validBranch)
            .when('isBankValid', {
                is: () => isBankValid === false,
                then: Yup.string().test('is-branchNumberValid', validBranch, (isBankValid) => isBankValid === false),
            }),

        institutionNumber: Yup.string()
            .required(validInstitution)
            .min(MIN_INSTITUTION_NUMBER_LENGTH, validInstitution)
            .when('isBankValid', {
                is: () => isBankValid === false,
                then: Yup.string().test(
                    'is-institutionNumberValid',
                    validInstitution,
                    (isBankValid) => isBankValid === false
                ),
            }),
        accountNumber: Yup.string().required(validAccount).min(MIN_ACCOUNT_NUMBER_LENGTH, validAccount),
    });
    const sendAdobeAnalyticsTransaction = (transactionName) => {
                        window.adobeDataLayer?.push({
                            event: ADOBE.TRANSACTION_COMPLETE,
                            transaction: {
                                transactionName,
                                transactionCategory: 'Member Profile',
                            }
                        });
                    };

    const { handleSubmit, values, touched, errors, isValid, submitCount, setFieldValue, setFieldTouched } = useFormik({
        initialValues: {
            branchNumber: member.bankAccount?.branchNumber || '',
            institutionNumber: member.bankAccount?.institutionNumber || '',
            accountNumber: member.bankAccount?.accountNumber || '',
            isBankValid,
        },
        onSubmit: async () => {
            const response = await dispatch(
                UpdateMemberBankAccount(values.branchNumber, values.institutionNumber, values.accountNumber)
            );
            if (response.success) {
                history.push('/gb/member-profile-2/personal');
            }
        },
        validationSchema,
    });

    useEffect(() => {
        if (isCiamSession && !isSteppedUp) {
            history.push('/gb/member-profile-2/personal');
        }
    }, [history, isCiamSession, isSteppedUp]);

    // Handle modal refresh and restore field values from state.
    useEffect(() => {
        if (!(isLoadingFeatures || isLoadingMemberData)) {
            setFieldValue('branchNumber', member.bankAccount?.branchNumber || '');
            setFieldValue('institutionNumber', member.bankAccount?.institutionNumber || '');
            setFieldValue('accountNumber', member.bankAccount?.accountNumber || '');
        }
    }, [isLoadingFeatures, isLoadingMemberData, member.bankAccount, setFieldValue]);

    // Scrolls to error messages at the top of the modal on submit.
    useEffect(() => {
        if (!isValid) {
            errorRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'start',
            });
        }
    }, [isValid]);

    // Update the formik value of isBankValid during any change. This is required since only formik value can be used
    // in yup validation conditionals.
    useEffect(() => {
        setFieldValue('isBankValid', isBankValid);
    }, [setFieldValue, isBankValid]);

    const DEBOUNCE_DELAY = 500;
    const debouncedBranchNumber = useDebounce(values.branchNumber, DEBOUNCE_DELAY);
    const debouncedInstitutionNumber = useDebounce(values.institutionNumber, DEBOUNCE_DELAY);

    // Will check to see if user enters valid bank details while entering in branch and institution number
    // useDebounce is used to debounce any fast changing value to prevent GetBank being fired on every keystroke
    useEffect(() => {
        dispatch(GetBank(debouncedBranchNumber, debouncedInstitutionNumber));
        if (isBankValid) {
            // Deleting touched property so if member makes a change to only one field only
            // that field will show an error instead of both. This is so we can estimate which
            // field caused isBankValid to fail. Delete both when valid so we can re-estimate
            // with every new interaction
            delete touched.branchNumber;
            delete touched.institutionNumber;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedBranchNumber, debouncedInstitutionNumber]);

    return (
        <Modal
            ariaLabelledBy="heading"
            id="editBankingModal"
            isOpen={true}
            onClose={() => {
                history.push('/gb/member-profile-2/personal');
                dispatch(ResetBank());
            }}
        >
            {(isLoadingFeatures || isLoadingMemberData) && (
                <BankModalLoadingIndicator data-testid="bankModalLoadingIndicator" />
            )}
            {!isLoadingFeatures && !isLoadingMemberData && (
                <form onSubmit={handleSubmit}>
                    <H2 id="heading" customStyle={{ elementStyle: { marginTop: '0' } }}>
                        {headerLabel}
                    </H2>
                    {errorLoad?.hasError && (
                        <ErrorMsg>
                            Problem: {errorLoad.message} Code: {errorLoad.code}
                        </ErrorMsg>
                    )}
                    {errorUpdate?.hasError && (
                        <ErrorMsg>
                            Problem: {errorUpdate.message} Code: {errorUpdate.code}
                        </ErrorMsg>
                    )}
                    <ValidationAlertWrapper id="bankingErrors" ref={errorRef} aria-hidden={!isValid} aria-live="polite">
                        {!isValid && submitCount > 0 && (
                            <MessageCard variant={MessageCard.VARIANT.PRIMARY_ALERT} titleText={errorSummaryTitle}>
                                {bankValidationMessage}
                            </MessageCard>
                        )}
                    </ValidationAlertWrapper>

                    {bankInformationMessage}
                    <ExpandablePanel variant={ExpandablePanel.VARIANT.SECONDARY} title={accordianTitle}>
                        <div>
                            <div>
                                <BoldLabel>{personalCheque}</BoldLabel>
                                <img
                                    // TODO https://manulife-cdn.atlassian.net/browse/CDTGBC20-1135
                                    // Fix cross origin issue introduced by helmet v5 and plus
                                    // can be removed when fix forward solution is found.
                                    crossOrigin="anonymous"
                                    src={`${AEM_IMG_URL}${chequeImagePath}`}
                                    alt={chequeAltText}
                                    width="85%"
                                />
                            </div>
                            <div>
                                <BoldLabel>{online}</BoldLabel>
                                <List>
                                    <li>{onlineInstructionOne}</li>
                                    <li>{onlineInstructionTwo}</li>
                                    <li>{onlineInstructionThree}</li>
                                </List>
                            </div>
                        </div>
                    </ExpandablePanel>

                    <TextInputBankWrapper>
                        <TextInput
                            id="branchNumber"
                            label={branchNumberLabel}
                            placeholder="12345"
                            customStyle={{
                                rootStyle: {
                                    width: '100%',
                                },
                            }}
                            value={values.branchNumber}
                            onChange={(newValue) => {
                                setFieldTouched('branchNumber', true);
                                setFieldValue('branchNumber', newValue);
                            }}
                            disabled={!features['bankAccount']}
                            mask="99999"
                            errors={
                                touched.branchNumber && [
                                    {
                                        id: 'branchNumber',
                                        msg: errors.branchNumber,
                                        isError: Boolean(errors.branchNumber),
                                    },
                                ]
                            }
                            ariaRequired
                        />
                        <TextInput
                            id="institutionNumber"
                            label={institutionNumberLabel}
                            placeholder="123"
                            customStyle={{
                                rootStyle: {
                                    width: '100%',
                                },
                            }}
                            value={values.institutionNumber}
                            onChange={(newValue) => {
                                setFieldTouched('institutionNumber', true);
                                setFieldValue('institutionNumber', newValue);
                            }}
                            disabled={!features['bankAccount']}
                            mask="999"
                            errors={
                                touched.institutionNumber && [
                                    {
                                        id: 'institutionNumber',
                                        msg: errors.institutionNumber,
                                        isError: Boolean(errors.institutionNumber),
                                    },
                                ]
                            }
                            ariaRequired
                        />
                        <TextInput
                            id="accountNumber"
                            label={accountNumberLabel}
                            placeholder="1234567"
                            customStyle={{
                                rootStyle: {
                                    width: '100%',
                                },
                            }}
                            value={values.accountNumber}
                            onChange={(newValue) => setFieldValue('accountNumber', newValue)}
                            onBlur={() => setFieldTouched('accountNumber', true)}
                            disabled={!features['bankAccount']}
                            mask={isCsr ? '' : '999999999999'}
                            errors={
                                touched.accountNumber && [
                                    {
                                        id: 'accountNumber',
                                        msg: errors.accountNumber,
                                        isError: Boolean(errors.accountNumber),
                                    },
                                ]
                            }
                            ariaRequired
                        />
                    </TextInputBankWrapper>
                    <BoldLabel>{bankNameLabel}</BoldLabel>
                    {isLoadingBankData && <FieldLoadingIndicator />}
                    {!isLoadingBankData && <span>{bankName ? bankName : bankNotFound}</span>}
                    <ButtonWrapper>
                        <Button
                            id="cancelBankingButton"
                            variant={Button.VARIANT.SECONDARY}
                            onClick={() => {
                                sendAdobeAnalyticsTransaction('Edit My Personal Information');
                                history.push('/gb/member-profile-2/personal');
                                dispatch(ResetBank());
                            }}
                        >
                            {cancelButton}
                        </Button>
                        <Button
                            id="updateBankingButton"
                            type="submit"
                            disabled={
                                isLoadingBankData ||
                                isSavingBankInformation ||
                                isCsr ||
                                !isBankValid ||
                                !features['bankAccount'] ||
                                debouncedBranchNumber !== values.branchNumber ||
                                debouncedInstitutionNumber !== values.institutionNumber
                            }
                            saving={isLoadingBankData || isSavingBankInformation}
                            onClick={() => sendAdobeAnalyticsTransaction('Edit My Personal Information')}
                        >
                            {saveButton}
                        </Button>
                    </ButtonWrapper>
                </form>
            )}
        </Modal>
    );
};

export default BankingModal;
